<template>
  <f7-page class="unified-payment-page" @page:beforeout="onPageAfterOut">
    <!-- Loading State -->
    <f7-block strong class="text-align-center bg-color-white" v-if="pageLoading">
      <f7-preloader color="multi" :size="42"></f7-preloader>
      <p class="margin-top-half">{{ $t('common.loadingPaymentMethods') }}</p>
    </f7-block>

    <!-- Error State -->
    <f7-block strong class="text-align-center bg-color-white" v-if="errorMessage && !pageLoading">
      <f7-icon f7="exclamationmark_triangle_fill" size="48" color="red" class="margin-bottom"></f7-icon>
      <p>{{ errorMessage }}</p>
      <f7-button fill color="red" @click="retryLoad">{{ $t('common.retry') }}</f7-button>
    </f7-block>

    <!-- Main Payment Flow -->
    <div v-if="!pageLoading && !errorMessage" class="layout_content">
      <div class="left_layout"></div>
      <div class="main_layout">
        <f7-navbar sliding no-shadow back-link>
          <f7-nav-title sliding>{{ pageTitle }}</f7-nav-title>
          <f7-nav-right>
            <navbarright></navbarright>
          </f7-nav-right>
        </f7-navbar>

        <f7-list dividers-ios strong-ios media-list>
          <!-- Campaign/Entity Context Header -->
          <f7-list-group v-if="paymentContext.entity || paymentContext.campaign">
            <f7-block strong inset class="bg-color-white payment-context-card">
              <div class="payment-context-header">
                <img v-if="paymentContext.entity?.logo" :src="paymentContext.entity.logo" class="entity-logo" />
                <div v-else class="entity-logo-placeholder">{{ paymentContext.entity?.name?.charAt(0)?.toUpperCase() || 'A' }}</div>
                <div class="entity-info">
                  <h4 class="no-margin">{{ paymentContext.entity?.name || paymentContext.campaign?.name || $t('common.payment') }}</h4>
                  <p class="no-margin text-color-gray" style="font-size: 12px;">
                    {{ getContextDescription() }}
                  </p>
                </div>
              </div>
              <div class="payment-context-amount">
                <span class="amount-label">{{ $t('common.amountToPay') }}</span>
                <span class="amount-value">{{ formatCurrency(displayAmount, paymentContext.currency) }}</span>
              </div>
            </f7-block>
          </f7-list-group>

          <!-- Payment Method Selection -->
          <f7-list-group v-if="availableMethods.length > 0 && !selectedMethod">
            <f7-block-title medium>{{ $t('common.selectPaymentMethod') }}</f7-block-title>

            <div class="payment-methods-grid">
              <f7-card
                v-for="method in availableMethods"
                :key="method.id"
                raised
                outline
                bg-color="white"
                :data-method-id="method.id"
                :class="['payment-method-card', { disabled: !isMethodEnabled(method), selected: selectedMethod?.id === method.id }]"
                @click="chooseMethod(method)"
                :style="{ cursor: isMethodEnabled(method) ? 'pointer' : 'not-allowed' }"
              >
                <f7-card-content class="text-align-center" :padding="false">
                  <div class="payment-method-icon">
                    <img v-if="method.logoUrl" :src="method.logoUrl" alt="" style="height: 40px; width: auto;" />
                    <f7-icon v-else :f7="getMethodIcon(method)" size="40" color="primary"></f7-icon>
                  </div>
                  <div class="payment-method-name">{{ method.description || method.methodName }}</div>
                  <div v-if="method.partnerFees" class="payment-method-fees">
                    {{ $t('common.fees') }}: {{ method.partnerFees }}%
                  </div>
                  <div v-if="method.limitDescription" class="payment-method-limits">
                    {{ method.limitDescription }}
                  </div>
                  <f7-badge v-if="!isMethodEnabled(method)" color="gray">{{ $t('common.unavailable') }}</f7-badge>
                </f7-card-content>
              </f7-card>
            </div>

            <f7-button v-if="secondaryMethods.length > 0" link class="extraNetworkListSelect margin-horizontal-half" @click="openExtraNetworks">
              {{ $t('common.morePaymentMethods') }} ({{ secondaryMethods.length }})
            </f7-button>
          </f7-list-group>

          <!-- Selected Payment Method Details -->
          <f7-list-group v-if="selectedMethod">
            <f7-block-title medium>{{ $t('common.paymentDetails') }}</f7-block-title>

            <f7-card outline inset class="margin-horizontal-half no-padding-horizontal" style="background-color:white">
              <f7-list-item
                :title="selectedMethod.description || selectedMethod.methodName"
                :text="getMethodDetailText()"
              >
                <template #media>
                  <img v-if="selectedMethod.logoUrl" :src="selectedMethod.logoUrl" :style="{
                    'border-radius': isAvocash ? '50px' : 'auto',
                    width: '32px', height: '32px'
                  }" />
                  <f7-icon v-else :f7="getMethodIcon(selectedMethod)" size="28" color="primary" />
                </template>
              </f7-list-item>
              <f7-list-item v-if="!isAvocash && !isBankTransfer" :text="$t('common.redirectToPaymentUrl')">
                <template #media>
                  <f7-icon ios="f7:square_arrow_right" md="material:open_in_new" />
                </template>
              </f7-list-item>
            </f7-card>

            <!-- Amount Input -->
            <f7-list-group>
              <f7-block-title medium>{{ $t('common.fillInPaymentDetails') }}</f7-block-title>

              <f7-list-input
                :label="$t('common.amount')"
                floating-label
                :placeholder="$t('common.enterAmount')"
                :min="selectedMethod.minAmount || 1"
                :max="getMaxAmount()"
                :step="stepAmount"
                pattern="[0-9]*"
                :info="getAmountInfo()"
                :error-message="getAmountError()"
                v-model:value="amount"
                type="number"
                name="amount"
                class="inputNumber"
                tabindex="1"
                validate
                required
                clear-button
              >
                <template #media>
                  {{ paymentCurrency || paymentContext.currency }}
                </template>
              </f7-list-input>

              <f7-list-input
                floating-label
                :label="$t('common.tag')"
                :info="$t('common.tagInfo')"
                :error-message="$t('common.tagInfo')"
                v-model:value="tag"
                name="tag"
                maxlength="100"
                validate
                required
                tabindex="2"
                type="text"
                clear-button
                resizable
              />

              <f7-list-input
                floating-label
                :label="$t('common.description')"
                :info="$t('common.transferDescInfo')"
                :error-message="$t('common.descriptionInfo')"
                v-model:value="description"
                name="description"
                validate
                required
                tabindex="3"
                type="text"
                clear-button
              />
            </f7-list-group>

            <!-- Payer/Recipient Details based on Payment Method -->
            <f7-list-group v-if="showPayerDetails">
              <f7-block-title>{{ $t('common.payerDetails') }}</f7-block-title>

              <!-- Mobile Money Fields -->
              <div v-if="isMobileMoney">
                <f7-list-input
                  ref="phoneInputRef"
                  :label="$t('common.phoneNumber')"
                  type="tel"
                  name="telephone"
                  input-id="mobileMoneyPhoneNumber"
                  @change="validatePhoneNumber()"
                  @blur="validatePhoneNumber()"
                  v-model:value="telephone"
                  :error-message="$t('common.providePhoneNumber')"
                  required
                  validate
                />

                <f7-list-item>
                  <span>{{ $t("common.use_otp_code") }}</span>
                  <f7-toggle
                    :disabled="!isPhoneNumberValid"
                    class="lock_phone_number-otp float-right"
                    name="lock_phone_number"
                    v-model:checked="lock_phone_number"
                    color="primary"
                  ></f7-toggle>
                </f7-list-item>
              </div>

              <!-- AVOCASH Wallet Selection -->
              <div v-if="isAvocash && senderAvailableWallets.length > 0">
                <f7-list-item class="selectApiWallet" valueEl="item-subtitle" :title="$t('common.selectAccount')" smart-select :smart-select-params="{
                  openIn: isTablet ? 'popover' : 'sheet',
                  sheetSwipeToClose: true,
                  closeOnSelect: true,
                }">
                  <select name="sender_wallet_id" v-model="sender_wallet_id">
                    <optgroup v-for="(apiWalletList, accountTypeKey, index) in userAccount.userAccountWallets" :key="index" :label="getAccountType(accountTypeKey)">
                      <option v-for="(actWallet, wIdx) in apiWalletList.filter(w => !w.shared && w.id !== paymentContext.receiverWalletId && w.currency === (paymentCurrency || paymentContext.currency))" :key="wIdx" :value="actWallet.id" :data-option-color="actWallet.main ? 'primary' : ''" :disabled="Number(actWallet.balance) < Number(amount || displayAmount)">
                        {{ actWallet.id }} {{ actWallet.tag }} ({{ formatCurrency(actWallet.balance, actWallet.currency) }})
                      </option>
                    </optgroup>
                  </select>
                </f7-list-item>
              </div>

              <!-- Credit Card Fields -->
              <div v-if="isCreditCard">
                <f7-list-input :label="$t('common.firstName')" floating-label type="text" name="firstName" :placeholder="$t('common.firstName')" v-model:value="firstName" :error-message="$t('common.provideFirstName')" required validate clear-button />
                <f7-list-input :label="$t('common.lastName')" floating-label type="text" name="lastname" :placeholder="$t('common.lastName')" v-model:value="lastName" :error-message="$t('common.provideLastName')" required validate clear-button />

                <div class="grid grid-cols-2">
                  <f7-list-input ref="cardPhoneInputRef" :label="$t('common.phoneNumber')" type="tel" name="telephone" input-id="cardPhoneNumber" @blur="validatePhoneNumber()" @change="validatePhoneNumber()" v-model:value="telephone" :error-message="$t('common.providePhoneNumber')" validate />
                  <f7-list-input :label="$t('common.email')" floating-label type="email" name="email" :placeholder="$t('common.email')" v-model:value="email" :error-message="$t('common.provideEmail')" required validate clear-button />
                </div>

                <f7-list-input :label="$t('common.address')" floating-label type="text" name="address" :placeholder="$t('common.address')" v-model:value="address" :error-message="$t('common.provideAddress')" required validate clear-button />

                <div class="grid grid-cols-2">
                  <f7-list-input :label="$t('common.locality')" floating-label type="text" name="city" :placeholder="$t('common.locality')" v-model:value="city" :error-message="$t('common.locality')" required validate clear-button />
                  <f7-list-input :label="$t('common.postCode')" floating-label type="text" name="postalCode" maxlength="5" :placeholder="$t('common.postCode')" v-model:value="postalCode" :error-message="$t('common.postCode')" required validate clear-button />
                </div>

                <div class="grid grid-cols-2">
                  <f7-list-input :label="$t('common.country')" floating-label type="text" name="country" maxlength="2" :placeholder="$t('common.country')" v-model:value="countryCode" :error-message="$t('common.country')" required validate clear-button />
                  <f7-list-input :label="$t('common.state')" floating-label type="text" name="state" maxlength="2" :placeholder="$t('common.state')" v-model:value="state" :error-message="$t('common.state')" required validate clear-button />
                </div>
              </div>

              <!-- Bank Transfer - Select Saved Bank Account -->
              <div v-if="isBankTransfer">
                <div v-if="userBankAccounts.STANDARD.length > 0 || userBankAccounts.BUSINESS.length > 0">
                  <f7-list-item :title="$t('bankAccount.mybankaccounts')" />
                  <div v-for="(accountsList, accountTypeKey, index) in userBankAccounts" :key="index">
                    <template v-if="accountsList.length > 0">
                      <f7-list-item group-title :title="getAccountType(accountTypeKey)" />
                      <f7-list-item v-for="(account, idx) in accountsList" :key="idx" radio :value="account.id" :disabled="account.status !== 'ACTIVE'" @change="(e) => { if (e.target.checked) selectedBankAccountId = account.id; }" radio-icon="start" :title="account.tag" :subtitle="$t('bankAccount.number') + ': ' + account.number" :text="account.bic ? $t('bankAccount.bic') + ': ' + account.bic : ''" name="selectedBankAccountId" :checked="account.main || account.id === selectedBankAccountId" :class="account.main || account.id === selectedBankAccountId ? 'border-color-primary highlighted_horiz' : ''">
                      </f7-list-item>
                    </template>
                  </div>
                </div>
                <f7-button outline fill class="margin-horizontal-half" @click="addNewBankAccount">{{ $t('bankAccount.addNewBankAccount') }}</f7-button>
              </div>
            </f7-list-group>

            <!-- Recipient Details for Cash Out/Transfer -->
            <f7-list-group v-if="showRecipientDetails">
              <f7-block-title>{{ $t('common.recipientDetails') }}</f7-block-title>

              <div v-if="isAvocash">
                <f7-list-item v-if="paymentContext.receiverWalletId" :title="paymentContext.receiverName" :subtitle="paymentContext.receiverWalletId" :text="paymentContext.receiverUsername" />

                <f7-list-item v-else class="selectReceiver" valueEl="item-subtitle" :title="$t('common.selectAccount')" smart-select :smart-select-params="{
                  openIn: isTablet ? 'popover' : 'sheet',
                  sheetSwipeToClose: true,
                  closeOnSelect: true,
                }">
                  <select name="receiver_userId" v-model="receiverUserId">
                    <option v-for="(user, index) in paymentContext.memberList" :key="index" :value="user.userId">
                      {{ user.name }}
                    </option>
                  </select>
                </f7-list-item>

                <f7-list-item v-if="receiverUserId && Object.keys(paymentContext.receiverWallets).length > 0" class="selectReceiverWallet" valueEl="item-subtitle" :title="$t('common.selectAccount')" smart-select :smart-select-params="{ openIn: isTablet ? 'popover' : 'sheet', sheetSwipeToClose: true, closeOnSelect: true }">
                  <select name="receiver_wallet_id" v-model="receiverWalletId">
                    <optgroup v-for="(apiWalletList, accountTypeKey, index) in paymentContext.receiverWallets" :key="index" :label="getAccountType(accountTypeKey)">
                      <option v-for="(actWallet, wIdx) in apiWalletList.filter(w => w.id !== sender_wallet_id && w.currency === paymentCurrency)" :key="wIdx" :value="actWallet.id" :data-option-color="actWallet.main ? 'primary' : ''">
                        {{ actWallet.id }} {{ actWallet.tag }}
                      </option>
                    </optgroup>
                  </select>
                </f7-list-item>
              </div>

              <div v-if="isCinetpayCashOut">
                <f7-list-input ref="cashOutPhoneInputRef" :label="$t('common.phoneNumber')" type="tel" name="telephone" input-id="cinetpayCashOutphoneNumber" @change="validatePhoneNumber()" @blur="validatePhoneNumber()" v-model:value="telephone" :error-message="$t('common.providePhoneNumber')" required validate clear-button />
                <f7-list-input :label="$t('common.firstName')" floating-label type="text" name="firstName" :placeholder="$t('common.firstName')" v-model:value="firstName" :error-message="$t('common.provideFirstName')" required validate clear-button />
                <f7-list-input :label="$t('common.lastName')" floating-label type="text" name="lastname" :placeholder="$t('common.lastName')" v-model:value="lastName" :error-message="$t('common.provideLastName')" required validate clear-button />
                <f7-list-input :label="$t('common.email')" floating-label type="email" name="email" :placeholder="$t('common.email')" v-model:value="email" :error-message="$t('common.provideEmail')" required validate clear-button />
              </div>
            </f7-list-group>

            <!-- Action Buttons -->
            <f7-block class="payment-actions">
              <f7-button
                v-if="canProceed"
                :color="actionButtonColor"
                raised
                fill
                large
                :disabled="isProcessing"
                class="buttons no-margin-vertical"
                round-ios
                ripple
                @click="processPayment"
                strong
              >
                <f7-preloader v-if="isProcessing" color="white" size="20" />
                <span v-else>{{ actionButtonText }}</span>
              </f7-button>

              <f7-button
                v-if="selectedMethod && !canProceed"
                fill
                large
                disabled
                class="buttons no-margin-vertical"
                round-ios
              >
                {{ $t('common.fillRequiredFields') }}
              </f7-button>

              <f7-button outline fill large class="buttons no-margin-vertical margin-top" round-ios @click="clearSelection">
                {{ $t('common.changePaymentMethod') }}
              </f7-button>
            </f7-block>
          </f7-list-group>
        </f7-list>
      </div>
      <div class="right_layout"></div>
    </div>

    <!-- Stripe Payment Popup -->
    <AdaptiveSheet class="payWithStripePopup" :close-by-backdrop-click="false" :close-by-outside-click="false" tablet-fullscreen swipe-to-close :backdrop="false" style="min-height: 100%; --f7-popup-bg-color: #fff" @closed="onStripePopupClosed" full-screen>
      <f7-view>
        <f7-page>
          <div class="layout_content">
            <div class="left_layout"></div>
            <div class="main_layout">
              <f7-navbar back-link :back-link-url="'#'">
                <f7-nav-title>{{ $t("common.payWith", { paymentMethod: selectedMethod?.description || 'Stripe' }) }}</f7-nav-title>
              </f7-navbar>
              <f7-list dividers-ios strong-ios media-list>
                <f7-list-group v-if="paymentTxStore.currentRequest">
                  <f7-card outline header-divider raised :class="!isTablet ? 'margin-horizontal-half' : 'no-margin-horizontal'">
                    <f7-card-header>{{ $t("common.paymentDetails") }}</f7-card-header>
                    <f7-list-item :header="$t('common.amount')" :title="formatCurrency(paymentTxStore.currentRequest.amount, paymentTxStore.currentRequest.currency)" />
                    <f7-list-item :header="$t('common.tag')" :title="paymentTxStore.currentRequest.referenceNote" />
                    <f7-list-item :header="$t('common.description')" :title="paymentTxStore.currentRequest.description" />
                  </f7-card>
                </f7-list-group>
              </f7-list>

              <StripCheckout
                v-if="stripeClientSecret"
                ref="stripeComponent"
                :stripeAccountId="stripeAccountId"
                :clientSecret="stripeClientSecret"
                @payment-success="onStripeSuccess"
                @payment-error="onStripeError"
              ></StripCheckout>
            </div>
            <div class="right_layout"></div>
          </div>
        </f7-page>
      </f7-view>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { f7 } from 'framework7-vue';
import { storeToRefs } from 'pinia';
import Navbarright from '@/components/navbarright.vue';
import StripCheckout from '@/components/stripeCheckout.vue';
import i18n from '@/js/i18n.js';
import { useAccount } from '@/js/composables/useAccount.js';
import { usePayment } from '@/js/composables/usePayment.js';
import { useTransactAuth } from '@/js/composables/useTransactAuth.js';
import { useTabletScreen } from '@/js/composables/useMediaQuery.js';
import { useTelephone } from '@/js/composables/useTelephone.js';
import { useHaptics } from '@/js/composables/useHaptics.js';
import { useAuthStore } from '@/js/pinia-stores/auth.js';
import { useApimoneyStore } from '@/js/pinia-stores/apimoney.js';
import { useStripeStore } from '@/js/pinia-stores/stripe.js';
import { useKeycloakStore } from '@/js/pinia-stores/keycloak.js';
import { usePaymentTransactionStore } from '@/js/pinia-stores/paymentTransaction.js';
import { openAdaptiveModal, closeAdaptiveModal } from '../js/composables/useAdaptiveModal.js';
import AdaptiveSheet from '../components/common/AdaptiveSheet.vue';

const props = defineProps({
  f7route: Object,
  f7router: Object,
});

const { f7route, f7router } = props;
const t = i18n.global.t;

const authStore = useAuthStore();
const apimoneyStore = useApimoneyStore();
const stripeStore = useStripeStore();
const keycloakStore = useKeycloakStore();
const paymentTxStore = usePaymentTransactionStore();
const { profile } = storeToRefs(authStore);

const isTablet = useTabletScreen().matches;

// Several type="tel" inputs share this page (behind v-if): each gets its own
// intl-tel-input instance, initialized when it appears.
const phoneInputRef = ref(null);
const cardPhoneInputRef = ref(null);
const cashOutPhoneInputRef = ref(null);
const countryPickerLabels = {
  title: t('common.selectCountry'),
  searchPlaceholder: t('common.search'),
  close: t('common.close'),
};
const mobileMoneyTel = useTelephone(phoneInputRef, { countryPicker: countryPickerLabels });
const creditCardTel = useTelephone(cardPhoneInputRef, { countryPicker: countryPickerLabels });
const cashOutTel = useTelephone(cashOutPhoneInputRef, { countryPicker: countryPickerLabels });

const {
  baseTransfer,
  payment_detail,
  paymentCurrency,
  selected_method,
  sender_wallet_id,
  telephone,
  lock_phone_number,
  firstName,
  lastName,
  email,
  address,
  city,
  state,
  countryCode,
  postalCode,
  userAccount,
  userDetail,
  senderAvailableWallets,
  stripeAccountId,
  selectedCountry,
  setPhoneNumber,
} = usePayment({ f7route, f7router });

// ============================================
// Local phone validation (fed by useTelephone)
// intl-tel-input instances live in the composables above; this page derives
// the dial code (phoneCode), validity flag and prefill sync from the active
// field (mobile-money / credit-card / cash-out). Declared before the
// isMobileMoney/isCreditCard/isCinetpayCashOut computeds — computeds evaluate
// lazily so the reference is safe.
// ============================================
const phoneCode = ref('');
const isPhoneNumberValid = ref(false);

const activeTel = computed(() => {
  if (isCinetpayCashOut.value) return cashOutTel;
  if (isCreditCard.value) return creditCardTel;
  return mobileMoneyTel;
});

const activePhoneData = computed(() => {
  const data = activeTel.value?.phoneData;
  if (data && data.isoCode) return data;
  if (isCinetpayCashOut.value) return cashOutTel.phoneData;
  if (isCreditCard.value) return creditCardTel.phoneData;
  return mobileMoneyTel.phoneData;
});

function activeTelInput() {
  const refToUse = isCinetpayCashOut.value
    ? cashOutPhoneInputRef
    : isCreditCard.value
      ? cardPhoneInputRef
      : phoneInputRef;
  const root = refToUse?.value?.$el || refToUse?.value || null;
  return root?.querySelector?.('input') || null;
}

function validatePhoneNumber() {
  const data = activePhoneData.value;
  phoneCode.value = data?.countryCode ? String(data.countryCode).replace(/^\+/, '') : phoneCode.value;
  isPhoneNumberValid.value = Boolean(data?.isValid);
}

async function pushTelephoneToInput() {
  // The chosen method's tel field sits behind v-if; wait for it (and its
  // intl-tel-input instance) to mount before prefilling.
  const found = await waitFor(() => activeTelInput() && activeTel.value?.instance?.(), 1000, 100);
  if (!found) {
    nextTick(() => validatePhoneNumber());
    return;
  }
  const iti = activeTel.value.instance();
  if (typeof iti.setNumber === 'function' && telephone.value) {
    iti.setNumber(telephone.value);
  }
  const input = activeTelInput();
  if (input && typeof input.dispatchEvent === 'function') {
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
  nextTick(() => validatePhoneNumber());
}

const { getAccountType, addNewBankAccount: navigateAddBankAccount } = useAccount({ f7route, f7router });

// ============================================
// Local state
// ============================================
const pageLoading = ref(true);
const errorMessage = ref('');
const isProcessing = ref(false);
const showStripePopup = ref(false);
const availableMethods = ref([]);
const secondaryMethods = ref([]);
const selectedMethod = ref(null);
const amount = ref(0);
const tag = ref('');
const description = ref('');
const selectedBankAccountId = ref('');
const receiverUserId = ref('');
const receiverWalletId = ref('');
const stripeClientSecret = ref('');

// ============================================
// Auth composable (biometric/passkey/step-up)
// Must be created before functions it references are invoked.
// ============================================
const transactAuth = useTransactAuth({
  f7route,
  f7router,
  baseTransfer,
  userPaymentProvider: selectedMethod,
  payment_detail,
  paymentPageSuccessCallback: authorizeAfterAuth,
  stripeValidate: null,
});
const { checkAuth, validatedDialog } = transactAuth;
const haptic = useHaptics();

// ============================================
// Payment context
// ============================================
const CONTRIBUTION_TRX_TYPES = ['TONT', 'ASSO', 'SAVB', 'GBUY', 'WALL', 'ADCP'];

const paymentContext = computed(() => ({
  entity: payment_detail.value?.entity || null,
  campaign: payment_detail.value?.project || null,
  currency: payment_detail.value?.paymentCurrency || baseTransfer.value?.currency || 'XOF',
  senderWalletId: baseTransfer.value?.sender_wallet_id || sender_wallet_id.value,
  receiverWalletId: baseTransfer.value?.receiver_wallet_id || receiverWalletId.value,
  receiverName: userDetail.value?.name || '',
  receiverUsername: userDetail.value?.username || '',
  receiverWallets: userDetail.value?.userAccountWallets || {},
  memberList: payment_detail.value?.entity?.memberList || [],
  transactionType: String(baseTransfer.value?.transaction_type || payment_detail.value?.transaction_type || 'CASH_IN').toUpperCase(),
  trxType: baseTransfer.value?.trxType || '',
}));

const isContribution = computed(() => CONTRIBUTION_TRX_TYPES.includes(String(paymentContext.value.trxType).toUpperCase()));
const isCashIn = computed(() => paymentContext.value.transactionType === 'CASH_IN');
const isAvocash = computed(() => partnerOf(selectedMethod.value) === 'AVOCASH');
const isMobileMoney = computed(() => ['MOBILE_MONEY', 'WALLET'].includes(selectedMethod.value?.paymentMethod));
const isCreditCard = computed(() => ['CREDIT_CARD', 'ALL'].includes(selectedMethod.value?.paymentMethod));
const isBankTransfer = computed(() => selectedMethod.value?.paymentMethod === 'BANK_TRANSFER');
const isCinetpayCashOut = computed(() =>
  !isCashIn.value &&
  partnerOf(selectedMethod.value) === 'CINETPAY' &&
  isMobileMoney.value
);

function partnerOf(method) {
  return String(method?.paymentPartner ?? '').toUpperCase();
}

const pageTitle = computed(() => {
  if (isCashIn.value) return isContribution.value ? t('common.contribute') : t('common.topUp');
  if (paymentContext.value.transactionType === 'CASH_OUT') return t('common.withdraw');
  return t('common.payment');
});

const actionButtonText = computed(() => {
  if (isCashIn.value) return isContribution.value ? t('common.contribute') : t('common.pay');
  if (paymentContext.value.transactionType === 'CASH_OUT') return t('common.redeem');
  return t('common.pay');
});

const actionButtonColor = computed(() => (isCashIn.value ? 'red' : 'green'));

const displayAmount = computed(() => {
  const a = Number(amount.value);
  if (!isNaN(a) && a > 0) return a;
  return Number(baseTransfer.value?.amount || payment_detail.value?.setAmount || 0);
});

const stepAmount = computed(() => 5);

const showPayerDetails = computed(() =>
  selectedMethod.value &&
  isCashIn.value &&
  (isMobileMoney.value || isCreditCard.value || isAvocash.value || isBankTransfer.value)
);

const showRecipientDetails = computed(() =>
  !!selectedMethod.value && ['CASH_OUT', 'TRANSFER', 'AIRTIME'].includes(paymentContext.value.transactionType)
);

const userBankAccounts = computed(() => ({
  STANDARD: userAccount.value?.userBankAccounts?.STANDARD || [],
  BUSINESS: userAccount.value?.userBankAccounts?.BUSINESS || [],
}));

// ============================================
// Validation & UI helpers
// ============================================
const canProceed = computed(() => {
  if (!selectedMethod.value) return false;
  if (!(displayAmount.value > 0)) return false;
  if (selectedMethod.value.minAmount && displayAmount.value < Number(selectedMethod.value.minAmount)) return false;
  if (selectedMethod.value.maxAmount && displayAmount.value > Number(selectedMethod.value.maxAmount)) return false;

  if (isMobileMoney.value && !(isPhoneNumberValid.value && telephone.value && telephone.value.replace(/\D/g, '').length >= 8)) return false;
  if (isCreditCard.value && !(firstName.value && lastName.value && email.value && telephone.value && address.value && city.value && postalCode.value && countryCode.value && state.value)) return false;
  if (isAvocash.value && isCashIn.value && senderAvailableWallets.value.length > 0 && !sender_wallet_id.value) return false;
  if (isBankTransfer.value && !selectedBankAccountId.value) return false;
  if (showRecipientDetails.value && isAvocash.value && !paymentContext.value.receiverWalletId && !receiverWalletId.value) return false;
  if (!(String(tag.value || baseTransfer.value?.tag || '').length > 0)) return false;
  return true;
});

function getMaxAmount() {
  if (!selectedMethod.value) return 999999999;
  if (Number(selectedMethod.value.maxAmountUncapped) > 0) return Number(selectedMethod.value.maxAmountUncapped);
  return Number(selectedMethod.value.maxAmount) || 999999999;
}

function getAmountInfo() {
  if (selectedMethod.value?.limitDescription) return selectedMethod.value.limitDescription;
  return t('common.enterAmount');
}

function getAmountError() {
  return getAmountInfo();
}

function getMethodDetailText() {
  if (!selectedMethod.value) return '';
  const parts = [];
  if (selectedMethod.value.limitDescription) parts.push(selectedMethod.value.limitDescription);
  if (selectedMethod.value.partnerFees) parts.push(`${selectedMethod.value.partnerFees}% ${t('common.fees')}`);
  return parts.join(' • ');
}

function getMethodIcon(method) {
  const icons = {
    MOBILE_MONEY: 'phone_fill',
    CREDIT_CARD: 'creditcard_fill',
    WALLET: 'wallet2',
    AVOCASH: 'bolt_fill',
    BANK_TRANSFER: 'building_2_fill',
    TRANSFER: 'arrow_left_right',
    ALL: 'creditcard_fill',
  };
  return icons[method?.paymentMethod] || 'creditcard_fill';
}

function isMethodEnabled(method) {
  if (!method.available) return false;
  if (partnerOf(method) === 'AVOCASH' && method.paymentMethod === 'AVOCASH' && isCashIn.value && senderAvailableWallets.value.length < 1) return false;
  if (Number(method.minAmount) > 0 && Number(method.maxAmount) > 0 && Number(method.minAmount) > Number(method.maxAmount)) return false;
  return true;
}

function formatCurrency(value, currency) {
  const num = Number(value);
  if (isNaN(num)) return '';
  try {
    return new Intl.NumberFormat(profile.value?.locale || 'fr-FR', {
      style: 'currency',
      currency: currency || 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  } catch (e) {
    return `${num} ${currency || ''}`.trim();
  }
}

function getContextDescription() {
  const ctx = paymentContext.value;
  if (ctx.entity && ctx.campaign && ctx.campaign.name !== ctx.entity.name) {
    return `${ctx.campaign.name} • ${ctx.entity.name}`;
  }
  if (ctx.campaign) return ctx.campaign.name;
  if (ctx.entity) return ctx.entity.name;
  return '';
}

// ============================================
// Load methods
// ============================================
async function onPageLoad() {
  errorMessage.value = '';
  await waitForPaymentContext();

  try {
    const ctx = paymentContext.value;
    const currencyCode = ctx.currency;
    const ctryCode =
      ctx.entity?.countryCode ||
      ctx.campaign?.countryCode ||
      baseTransfer.value?.countryCode ||
      userAccount.value?.countryCode ||
      keycloakCountryCode() ||
      'CI';

    await loadPaymentMethods(currencyCode, ctryCode);

    if (availableMethods.value.length === 0) {
      errorMessage.value = t('common.failedToLoadPaymentMethods');
      return;
    }

    // Pre-select default: prefer AVOCASH wallet for cash-in, else first enabled
    const preferred =
      availableMethods.value.find((m) => isMethodEnabled(m) && partnerOf(m) === 'AVOCASH' && m.paymentMethod === 'AVOCASH' && isCashIn.value) ||
      availableMethods.value.find((m) => isMethodEnabled(m));
    if (preferred) await chooseMethod(preferred);
  } catch (error) {
    console.error('Failed to load payment methods:', error);
    errorMessage.value = t('common.failedToLoadPaymentMethods');
  } finally {
    pageLoading.value = false;
  }
}

function keycloakCountryCode() {
  return keycloakStore.userInfo?.countryCode || keycloakStore.tokenParsed?.countryCode || null;
}

async function loadPaymentMethods(currencyCode, countryCode) {
  await apimoneyStore.getPaymentTransferMethods({ currencyCode, countryCode });
  const all = apimoneyStore.paymentTransferMethods || [];
  const tt = paymentContext.value.transactionType;

  const normalized = all
    .filter((m) => m.available && (m.transactionType === tt || m.transactionType === 'ALL'))
    .map((m, i) => ({
      ...m,
      id: `${m.paymentPartner}_${m.methodName || m.paymentMethod}_${i}`,
      paymentPartner: m.paymentPartner,
    }));

  const primaryCount = 6;
  availableMethods.value = normalized.slice(0, primaryCount);
  secondaryMethods.value = normalized.slice(primaryCount);
}

async function openExtraNetworks() {
  const buttons = [{ text: t('common.selectPaymentMethod'), label: true }];
  secondaryMethods.value.forEach((network) => {
    buttons.push({
      text: network.description || network.methodName,
      icon: network.logoUrl ? `<img src="${network.logoUrl}" class="icon" width="24"/>` : undefined,
      onClick: async () => {
        const lastItem = availableMethods.value.pop();
        const idx = secondaryMethods.value.indexOf(network);
        secondaryMethods.value.splice(idx, 1);
        availableMethods.value.push(network);
        secondaryMethods.value.push(lastItem);
        await chooseMethod(network);
      },
    });
  });
  buttons.push({ text: t('common.cancel'), color: 'red' });
  f7.actions.create({ convertToPopover: true, closeByOutsideClick: true, closeOnEscape: true, buttons }).open();
}

// ============================================
// Selection
// ============================================
async function chooseMethod(method) {
  if (!method || !isMethodEnabled(method)) return;

  selectedMethod.value = method;
  selected_method.value = method.methodName || method.paymentMethod;
  apimoneyStore.setUserPaymentProvider(method);

  paymentCurrency.value = method.currencyCode || paymentContext.value.currency;
  selectedCountry.value = method.countryCode || selectedCountry.value;
  phoneCode.value = method.phoneCode || phoneCode.value;

  await nextTick();
  setPhoneNumber(method);
  await pushTelephoneToInput();

  if (['CREDIT_CARD', 'ALL'].includes(method.paymentMethod) && userAccount.value?.standard) {
    apimoneyStore.getAllCreditCards(userAccount.value.standard.id);
  }
}

function clearSelection() {
  selectedMethod.value = null;
  selected_method.value = '';
  amount.value = 0;
  tag.value = '';
  description.value = '';
  telephone.value = '';
  sender_wallet_id.value = '';
  selectedBankAccountId.value = '';
  receiverUserId.value = '';
  receiverWalletId.value = '';
  lock_phone_number.value = false;
}

// ============================================
// Payment execution
// ============================================
async function processPayment() {
  if (!canProceed.value || isProcessing.value) return;

  isProcessing.value = true;
  f7.preloader.show('multi');

  try {
    const partner = partnerOf(selectedMethod.value);
    const method = selectedMethod.value.paymentMethod;

    if (partner === 'STRIPE' && (method === 'CREDIT_CARD' || method === 'ALL')) {
      await processStripePayment();
    } else if (
      ['CINETPAY', 'FEEXPAY', 'PAYDUNYA', 'BIZAO', 'ORANGE_MONEY', 'MTN_MOBILE_MONEY', 'WAVE', 'MOOV_MONEY', 'FREE_MONEY'].includes(partner) &&
      (method === 'MOBILE_MONEY' || method === 'WALLET' || method === 'CREDIT_CARD' || method === 'ALL')
    ) {
      await processRedirectProviderPayment();
    } else if (partner === 'AVOCASH' || method === 'AVOCASH') {
      await processAvocashPayment();
    } else if (method === 'BANK_TRANSFER') {
      await processBankTransfer();
    } else {
      throw new Error(t('common.unsupportedPaymentMethod'));
    }
  } catch (error) {
    console.error('Payment processing error:', error);
    haptic.error();
    f7.dialog.alert(error?.message || t('common.paymentFailed'));
  } finally {
    isProcessing.value = false;
    f7.preloader.hide();
  }
}

// ============================================
// PaymentTransactionRequest builder (backend DTO)
// ============================================
const TRX_TYPE_TO_ENTITY = {
  WALL: 'WALLET_TOPUP',
  TONT: 'CONTRIBUTION',
  ASSO: 'CAMPAIGN',
  SAVB: 'SAVE_TO_BUY',
  GBUY: 'GROUP_BUY',
  ADCP: 'AD_CAMPAIGN',
};

function mapEntityType() {
  if (!isCashIn.value) return 'PAYOUT';
  return TRX_TYPE_TO_ENTITY[String(paymentContext.value.trxType || '').toUpperCase()] || 'CAMPAIGN';
}

function mapEntityId(entityType) {
  const ctx = paymentContext.value;
  const legacy = baseTransfer.value || {};
  switch (entityType) {
    case 'AD_CAMPAIGN':
      return legacy.referenceId || ctx.campaign?.id;
    case 'GROUP_BUY':
      return legacy.groupId || legacy.referenceId || ctx.entity?.id;
    case 'WALLET_TOPUP':
    case 'PAYOUT':
      return receiverWalletId.value || legacy.receiver_wallet_id || ctx.receiverWalletId;
    default:
      return legacy.referenceId || ctx.campaign?.id || ctx.entity?.id;
  }
}

/**
 * Format TRX-yyyyMMdd-HHmmss-XXXXXX — matches the backend generator so
 * externally supplied references stay consistent. Reusing the same
 * reference on retries makes the operation idempotent (unique DB constraint).
 */
function newTransactionReference() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `TRX-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(
    d.getMinutes()
  )}${pad(d.getSeconds())}-${rand}`;
}

/**
 * Builds the backend PaymentTransactionRequest DTO from page state.
 * The legacy hand-off payload (baseTransfer from __piniaLcStorage) only
 * supplies context (trxType, wallets, references); execution goes through
 * the unified /payments endpoints exclusively.
 */
function buildPaymentTransactionRequest() {
  const ctx = paymentContext.value;
  const m = selectedMethod.value;
  const legacy = baseTransfer.value || {};
  const isPayIn = isCashIn.value;

  const entityType = mapEntityType();
  const entityId = mapEntityId(entityType);
  const payerName =
    [firstName.value, lastName.value].filter(Boolean).join(' ') || userAccount.value?.name || '';
  const phoneDigits = telephone.value
    ? `${phoneCode.value || ''}${telephone.value}`.replace(/\D/g, '')
    : '';
  const origin = window.location.origin;
  // One stable reference per logical attempt — reused across auth retries
  // (idempotency) and embedded in returnUrl/cancelUrl so provider
  // round-trips land on the status page with a resolvable transaction.
  const requestReference = legacy.idempotency_key || legacy.partner_ref || newTransactionReference();

  // Wallet semantics per DTO docs:
  //   PAY_IN : walletId = funding source, beneficiaryWalletId = destination
  //   PAY_OUT: walletId = credited target, beneficiaryWalletId = debited source
  const walletId = isPayIn
    ? sender_wallet_id.value || legacy.sender_wallet_id || undefined
    : receiverWalletId.value || legacy.receiver_wallet_id || ctx.receiverWalletId || undefined;
  const beneficiaryWalletId = isPayIn
    ? receiverWalletId.value || legacy.receiver_wallet_id || ctx.receiverWalletId || undefined
    : legacy.sender_wallet_id || undefined;

  const request = {
    transactionReference: requestReference,
    amount: displayAmount.value,
    fees: 0,
    currency: m.currencyCode || ctx.currency,
    paymentType: isPayIn ? 'PAY_IN' : 'PAY_OUT',
    paymentPartner: partnerOf(m),
    paymentMethod: m.paymentMethod === 'ALL' ? 'CREDIT_CARD' : m.paymentMethod,
    entityType,
    entityId,
    groupId: entityType === 'GROUP_BUY' ? (legacy.groupId || ctx.entity?.id) : undefined,
    campaignId: ['CAMPAIGN', 'CONTRIBUTION', 'SAVE_TO_BUY'].includes(entityType)
      ? (legacy.referenceId || ctx.campaign?.id)
      : undefined,
    adCampaignId: entityType === 'AD_CAMPAIGN' ? entityId : undefined,
    contributionId: entityType === 'CONTRIBUTION' ? entityId : undefined,
    userId: keycloakStore.subject || profile.value?.sub || undefined,
    accountId:
      legacy.payer_account_id ||
      userAccount.value?.standard?.id ||
      userAccount.value?.business?.id ||
      undefined,
    walletId,
    beneficiaryId: !isPayIn && receiverUserId.value ? receiverUserId.value : undefined,
    beneficiaryWalletId,
    payerPhoneNumber: isPayIn && phoneDigits ? phoneDigits : undefined,
    payerEmail: isPayIn ? email.value || undefined : undefined,
    payerName: isPayIn ? payerName || undefined : undefined,
    beneficiaryPhoneNumber: !isPayIn && phoneDigits ? phoneDigits : undefined,
    beneficiaryEmail: !isPayIn ? email.value || undefined : undefined,
    beneficiaryName: !isPayIn ? ctx.receiverName || undefined : undefined,
    userDisplayName: payerName || undefined,
    userAddress:
      [address.value, city.value, postalCode.value, state.value].filter(Boolean).join(', ') ||
      undefined,
    userCountryCode: countryCode.value || selectedCountry.value || keycloakCountryCode() || undefined,
    returnUrl: `${origin}/transaction/sucess/${requestReference}`,
    cancelUrl: `${origin}/transaction/failure/${requestReference}`,
    description: description.value || legacy.description || '',
    referenceNote: tag.value || legacy.tag || '',
    paymentLinkId: f7route?.params?.paymentLinkId || undefined,
    providerClientKey: legacy.idempotency_key || undefined,
    requiresApproval: !isPayIn ? true : undefined,
    metadata: {
      trxType: ctx.trxType || legacy.trxType,
      entityName: ctx.entity?.name,
      campaignName: ctx.campaign?.name,
      operator: m.operator,
      cinetTransferMethod: m.paymentMethod === 'WALLET' ? m.methodName : undefined,
      lockPhoneNumber: lock_phone_number.value,
      bankAccountId: isBankTransfer.value ? selectedBankAccountId.value : undefined,
      source: 'vue-payment-page',
    },
  };

  Object.keys(request).forEach((k) => request[k] === undefined && delete request[k]);
  return request;
}

// ============================================
// Submission & response routing
// ============================================
async function submitPayment({ reuseStoredRequest = false } = {}) {
  let response;
  if (reuseStoredRequest && paymentTxStore.currentRequest) {
    response = await paymentTxStore.submit();
  } else {
    response = await paymentTxStore.submit(buildPaymentTransactionRequest());
  }
  await handleInitiateResponse(response);
  return response;
}

/**
 * Routes the initiate response:
 *  - returnUrl            -> provider hosted-checkout redirect
 *  - providerSessionToken -> Stripe hosted-elements popup
 *  - SUCCESS              -> success screen
 *  - FAILED/EXPIRED/...   -> error dialog
 *  - PENDING / PROCESSING -> status screen
 */
async function handleInitiateResponse(response) {
  if (!response) throw new Error(t('common.paymentFailed'));

  if (response.returnUrl) {
    await redirectExternal(response.returnUrl);
    return;
  }

  if (response.providerSessionToken) {
    stripeClientSecret.value = response.providerSessionToken;
    showStripePopup.value = true;
    await nextTick();
    openAdaptiveModal('.payWithStripePopup');
    return;
  }

  if (response.isSuccessful) {
    haptic.success();
    validatedDialog();
    setTimeout(() => {
      props.f7router.navigate(`/transaction/sucess/${response.transactionReference}`, { reloadCurrent: true });
    }, 1200);
    return;
  }

  if (response.isFailed) {
    haptic.error();
    throw new Error(response.failureReason || t('common.paymentFailed'));
  }

  props.f7router.navigate(`/transaction/pending/${response.transactionReference}`, { reloadCurrent: true });
}

async function redirectExternal(url) {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isPluginAvailable('Browser')) {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
      return;
    }
  } catch (e) {
    // native Browser plugin unavailable — fall through to web redirect
  }
  window.location.assign(url);
}

/** STRIPE hosted-elements flow */
async function processStripePayment() {
  await stripeStore.getStripePublicKey();
  await submitPayment();
}

/**
 * CINETPAY / FEEXPAY / PAYDUNYA / BIZAO / mobile-money partners and any
 * flow that requires step-up authentication first. The request is built
 * and persisted before the auth round-trip so it can be resubmitted
 * verbatim afterwards (same transactionReference -> idempotent).
 */
async function processRedirectProviderPayment() {
  paymentTxStore.setRequest(buildPaymentTransactionRequest());
  const authorized = await checkAuth({
    onSuccess: () => authorizeAfterAuth(),
  });
  if (authorized === false) {
    f7.toast.create({ text: t('common.paymentCancelled'), closeTimeout: 2000 }).open();
  }
}

/** Internal AVOCASH wallet transfer */
async function processAvocashPayment() {
  const limit = Number(userAccount.value?.stdPaymentLimit || 0);
  if (limit > 0 && displayAmount.value <= limit) {
    await submitPayment();
    return;
  }
  await processRedirectProviderPayment();
}

async function processBankTransfer() {
  if (!selectedBankAccountId.value) {
    f7.dialog.alert(t('common.selectBankAccount'));
    return;
  }
  await processRedirectProviderPayment();
}

/**
 * Called after successful PIN/biometric/passkey authentication.
 * Resubmits the stored request (idempotent by transactionReference);
 * falls back to rebuilding when no stored request exists.
 */
async function authorizeAfterAuth() {
  try {
    await submitPayment({ reuseStoredRequest: !!paymentTxStore.currentRequest });
  } catch (error) {
    console.error('Payment submission failed:', error);
    haptic.error();
    f7.dialog.alert(error?.message || t('common.paymentFailed'));
  }
}

// ============================================
// Stripe events
// ============================================
function onStripeSuccess(data) {
  showStripePopup.value = false;
  closeAdaptiveModal('.payWithStripePopup');
  haptic.success();
  validatedDialog();
  const reference =
    data?.transactionReference || paymentTxStore.currentResponse?.transactionReference;
  setTimeout(() => {
    props.f7router.navigate(`/transaction/sucess/${reference || ''}`, { reloadCurrent: true });
  }, 1200);
}

function onStripeError(error) {
  haptic.error();
  f7.dialog.alert(error?.message || t('common.paymentFailed'));
}

function onStripePopupClosed() {
  showStripePopup.value = false;
}

// ============================================
// Helpers
// ============================================
function waitFor(predicate, timeoutMs = 5000, intervalMs = 200) {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      let value;
      try {
        value = predicate();
      } catch (e) {
        value = null;
      }
      if (value) return resolve(value);
      if (Date.now() - startedAt >= timeoutMs) return resolve(null);
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

/**
 * Hydrates page context from a payment-trigger draft (campaign contribution,
 * ad top-up, listing purchase, cart checkout, payout...) when no legacy
 * __piniaLcStorage payload exists for this route. Maps the stored
 * PaymentTransactionRequest back into the refs the page already consumes.
 */
function hydrateFromTriggerDraft() {
  const draft = paymentTxStore.draft;
  if (!draft?.request) return false;
  const routeRef = f7route?.params?.paymentLinkId;
  if (routeRef) {
    // Route reference must match the draft exactly.
    if (!draft.reference || routeRef !== draft.reference) return false;
  } else if (!draft.createdAt || Date.now() - draft.createdAt > 30 * 60 * 1000) {
    // No route reference (e.g. /pay/) — only accept a fresh draft to
    // avoid hijacking the page with a stale persisted intent.
    return false;
  }

  const req = draft.request;
  const display = draft.display || {};
  baseTransfer.value = {
    transaction_type: req.paymentType === 'PAY_OUT' ? 'CASH_OUT' : 'CASH_IN',
    trxType: req.metadata?.trxType || '',
    referenceId: req.campaignId || req.adCampaignId || req.orderId || req.listingId || req.entityId,
    receiver_wallet_id: req.beneficiaryWalletId || undefined,
    sender_wallet_id: req.paymentType === 'PAY_IN' ? req.walletId : undefined,
    payer_account_id: req.accountId,
    currency: req.currency,
    amount: Number(req.amount),
    tag: req.referenceNote || '',
    description: req.description || '',
  };
  payment_detail.value = {
    paymentCurrency: req.currency,
    entity: display.entity || null,
    project: display.project || null,
    setAmount: Number(req.amount),
    tag: req.referenceNote || '',
    transaction_type: req.paymentType === 'PAY_OUT' ? 'cash_out' : 'cash_in',
    wallet: null,
  };
  amount.value = Number(req.amount) || 0;
  tag.value = req.referenceNote || '';
  description.value = req.description || '';
  return true;
}

async function waitForPaymentContext(maxWaitMs = 3000) {
  // usePayment's onMounted restores baseTransfer synchronously for the
  // localStorage path but asynchronously (~500ms) for the payment-link path.
  const found = await waitFor(() => baseTransfer.value, maxWaitMs, 150);
  if (!found && hydrateFromTriggerDraft()) {
    paymentTxStore.clearDraft();
    return;
  }
  if (!found && !f7route?.params?.paymentLinkId) {
    errorMessage.value = t('common.noPendingTransaction');
  }
  // Prefill editable fields from restored context
  if (found) {
    amount.value = Number(baseTransfer.value.amount || payment_detail.value?.setAmount || 0);
    tag.value = baseTransfer.value.tag || payment_detail.value?.tag || '';
    description.value = baseTransfer.value.description || payment_detail.value?.project?.description || '';
  }
}

function retryLoad() {
  pageLoading.value = true;
  onPageLoad();
}

function onPageAfterOut() {
  showStripePopup.value = false;
  clearSelection();
}

onMounted(onPageLoad);
</script>

<style scoped>
.unified-payment-page .toolbar.tabbar.tabbar-icons {
  background-color: transparent;
}

.payment-context-card {
  background: linear-gradient(135deg, var(--f7-theme-color) 0%, color-mix(in srgb, var(--f7-theme-color) 70%, #fff) 100%);
  color: white;
  border-radius: 16px;
  margin: 16px;
  padding: 20px;
}

.payment-context-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.entity-logo {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  object-fit: cover;
  background: white;
}

.entity-logo-placeholder {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
}

.entity-info h4 {
  font-size: 16px;
  font-weight: 600;
}

.payment-context-amount {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid rgba(255,255,255,0.2);
}

.amount-label {
  font-size: 13px;
  opacity: 0.9;
}

.amount-value {
  font-size: 24px;
  font-weight: 700;
}

.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  padding: 0 16px 16px;
}

.payment-method-card {
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.payment-method-card:not(.disabled):active {
  transform: scale(0.98);
}

.payment-method-card.selected {
  border-color: var(--f7-theme-color);
  box-shadow: 0 0 0 2px var(--f7-theme-color);
}

.payment-method-card.disabled {
  opacity: 0.5;
}

.payment-method-icon {
  margin: 16px 0 8px;
  display: flex;
  justify-content: center;
}

.payment-method-name {
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
  padding: 0 8px;
}

.payment-method-fees {
  font-size: 10px;
  color: var(--f7-theme-color);
  font-weight: 500;
}

.payment-method-limits {
  font-size: 9px;
  color: var(--f7-on-surface-variant, #8e8e93);
  padding: 0 8px 8px;
}

.payment-actions {
  padding: 16px;
}

@media screen and (max-width: 568px) {
  .payment-methods-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
